import { useEffect, type ReactElement } from 'react';
import { TermsBody, PrivacyBody, SecurityBody } from '../pages/Legal';

export type LegalDoc = 'confidentiality' | 'terms' | 'privacy';

const DOCS: Record<LegalDoc, { title: string; body: () => ReactElement }> = {
  // "Confidentiality Terms" is the consent label for the Trust & Security doc.
  confidentiality: { title: 'Confidentiality Terms', body: SecurityBody },
  terms: { title: 'Terms of Service', body: TermsBody },
  privacy: { title: 'Privacy Policy', body: PrivacyBody },
};

/**
 * Shows a legal document (Confidentiality Terms, Terms of Service, or Privacy
 * Policy) in an in-place scrollable modal, so links in the sign-up consent
 * row don't punt the user out to a separate page mid-signup. Reuses the exact
 * same body content as the standalone `/terms`, `/privacy`, `/security` pages
 * (no copy duplication) plus the `.legal-prose` typography.
 */
export function LegalModal({
  doc,
  onClose,
}: {
  doc: LegalDoc | null;
  onClose: () => void;
}) {
  // Close on Escape while open.
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doc, onClose]);

  if (!doc) return null;
  const { title, body: Body } = DOCS[doc];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 20, 0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 260, // above the auth flow
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--surface-page)',
          borderRadius: 16,
          width: '100%',
          maxWidth: 640,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 12px 60px rgba(0,0,0,0.18)',
          animation: 'legalDlg 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}
      >
        {/* Sticky header with the title + close, so the title stays visible
            while the long document scrolls. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid var(--border-hairline)',
            flexShrink: 0,
          }}
        >
          <h2 className="f-display" style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 24,
              lineHeight: 1,
              color: 'var(--text-decorative)',
              padding: 4,
            }}
          >
            &times;
          </button>
        </div>

        {/* Scrollable document body. */}
        <div className="legal-prose legal-modal__body" style={{ overflowY: 'auto', padding: '24px 28px 28px' }}>
          <Body />
        </div>
      </div>

      <style>{`
        @keyframes legalDlg {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: none; }
        }
        /* Legal document typography — mirrors the standalone pages' LegalShell,
           scoped here so the modal renders identically without the page chrome. */
        .legal-modal__body { font-size: 15.5px; line-height: 1.7; color: var(--ink); }
        .legal-modal__body h2 {
          font-family: var(--f-serif);
          font-size: 20px;
          margin: 32px 0 12px;
          font-weight: 500;
        }
        .legal-modal__body h2:first-child { margin-top: 0; }
        .legal-modal__body h3 { font-family: var(--f-sans); font-size: 15px; font-weight: 600; margin: 22px 0 8px; }
        .legal-modal__body p { margin: 0 0 16px; }
        .legal-modal__body ul { margin: 0 0 16px; padding-left: 22px; }
        .legal-modal__body li { margin: 6px 0; }
        .legal-modal__body strong { font-weight: 600; }
        .legal-modal__body em { color: var(--text-decorative); }
        .legal-modal__body table { width: 100%; border-collapse: collapse; margin: 0 0 16px; font-size: 14px; }
      `}</style>
    </div>
  );
}
